// Browser push notification setup -- added 2026-10-07. Called right
// after login (see components/Login/EmailVerify.js). Entirely best-
// effort: a user declining the permission prompt, an unsupported
// browser, or any step failing should never block login or throw an
// unhandled error into the rest of the app -- every exported function
// here catches its own errors and resolves to a plain outcome value
// instead of rejecting.
import {
  getVapidPublicKey,
  subscribeToPush,
} from "./ApiService";

// PushManager.subscribe() requires the VAPID public key as a Uint8Array,
// not the base64url string the backend hands back.
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const isPushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// Registers /sw.js if it isn't already. Safe to call more than once --
// the browser no-ops a duplicate registration for the same script/scope.
const registerServiceWorker = async () => {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (error) {
    console.error("Service worker registration failed", error);
    return null;
  }
};

// The actual permission prompt + subscribe flow, called once per login.
// Returns one of: "unsupported", "already-subscribed", "denied",
// "subscribed", "error" -- informational only, nothing currently acts on
// the return value, but a caller that wants to show a status message
// later has something to key off.
export const setupPushNotifications = async (employeeId) => {
  if (!isPushSupported()) return "unsupported";

  try {
    const registration = await registerServiceWorker();
    if (!registration) return "error";

    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      // Already subscribed from a previous login on this browser -- make
      // sure the backend still has it (e.g. in case the DB was reset)
      // rather than re-prompting for permission, which the browser
      // wouldn't do again anyway once granted.
      await subscribeToPush({
        employee_id: employeeId,
        subscription: existingSubscription.toJSON(),
        user_agent: navigator.userAgent,
      });
      return "already-subscribed";
    }

    // Only ever prompts if permission is still in the default
    // (un-decided) state -- requestPermission() resolves immediately
    // with the existing value if the user already granted or denied it
    // before, it does not re-prompt.
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return "denied";

    const vapidRes = await getVapidPublicKey();
    if (vapidRes.responseCode !== 200 || !vapidRes.public_key) return "error";

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidRes.public_key),
    });

    await subscribeToPush({
      employee_id: employeeId,
      subscription: subscription.toJSON(),
      user_agent: navigator.userAgent,
    });

    return "subscribed";
  } catch (error) {
    console.error("Push notification setup failed", error);
    return "error";
  }
};
